<template>
  <div class="component-palette">
    <div
      class="palette-section"
      v-for="(group, groupIndex) in groupedComponents"
      :key="groupIndex"
    >
      <div class="palette-section-title">
        {{ group.title }}
      </div>
      <draggable
        class="palette-list"
        :list="group.items"
        :group="{ name: 'form-parts', pull: 'clone', put: false }"
        :clone="cloneComponent"
        :animation="150"
      >
        <div
          class="palette-item"
          v-for="(item, itemIndex) in group.items"
          :key="itemIndex"
          :data-part-type="item.type"
        >
          <v-icon class="palette-icon" small>{{ item.icon }}</v-icon>
          <span class="palette-text">{{ item.name }}</span>
          <v-icon class="drag-handle" small>drag_indicator</v-icon>
        </div>
      </draggable>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import GlobalConstants from '@/globals'

export default {
  name: 'ComponentPalette',
  components: {
    draggable
  },
  data() {
    return {
      coreComponents: [
        { type: 's', name: 'Section', icon: 'mdi-folder', internal: true },
        { type: 'p', name: 'Paragraph', icon: 'mdi-format-paragraph', internal: true },
        { type: 'b', name: 'Page Break', icon: 'mdi-format-page-break', internal: true }
      ],
      esignatureComponent: { type: 'e', name: 'E-Signature', icon: 'mdi-signature-freehand', internal: true },
      listTableComponent: { type: 'q', name: 'List/Table', icon: 'mdi-table', internal: false, atomic_type: null, is_list: true },
      customFieldComponent: { type: 'q', name: 'Custom Field', icon: 'mdi-shape-plus', internal: false, atomic_type: 'custom', is_list: false },
      reusableFieldComponent: { type: 'reusable', name: 'Reusable Field', icon: 'mdi-repeat-variant', internal: true }
    }
  },
  computed: {
    groupedComponents() {
      // Map global atomic field types to our palette item format
      const fieldComponents = GlobalConstants.AtomicFieldTypes
        .filter(ft => ft.value !== 'regex')
        .map(ft => ({
          type: 'q',
          atomic_type: ft.value,
          name: ft.name,
          icon: this.getIconForFieldType(ft.value),
          internal: false
        }))
      
      const regexComponent = GlobalConstants.AtomicFieldTypes
        .filter(ft => ft.value === 'regex')
        .map(ft => ({
          type: 'q',
          atomic_type: ft.value,
          name: ft.name,
          icon: 'mdi-regex',
          internal: false
        }))[0] || { type: 'q', atomic_type: 'regex', name: 'Regular Expression', icon: 'mdi-regex', internal: false }

      return [
        {
          title: 'Structure',
          items: this.coreComponents
        },
        {
          title: 'Fields',
          items: [
            ...fieldComponents,
            this.listTableComponent,
            this.esignatureComponent
          ]
        },
        {
          title: 'Advanced',
          items: [
            this.customFieldComponent,
            this.reusableFieldComponent,
            regexComponent
          ]
        }
      ]
    }
  },
  methods: {
    getIconForFieldType(atomicType) {
      const iconMap = {
        'text': 'mdi-format-text',
        'ssn': 'mdi-account-key',
        'email': 'mdi-email',
        'textarea': 'mdi-message-text',
        'harddate': 'mdi-calendar',
        'dropdown': 'mdi-form-select',
        'radio': 'mdi-radiobox-marked',
        'checkbox': 'mdi-checkbox-marked',
        'number': 'mdi-numeric',
        'currency': 'mdi-currency-usd',
        'file': 'mdi-file-upload',
        'altesign': 'mdi-account-edit',
        'regex': 'mdi-regex'
      }
      return iconMap[atomicType] || 'mdi-help-circle'
    },
    cloneComponent(item) {
      // Create a plain object that represents the 'newPart' payload we need to emit later
      return {
        isNew: true, // flag to identify this is a palette dropped item
        part_type: item.type,
        atomic_type: item.internal ? null : item.atomic_type,
        is_list: item.is_list, // include is_list if present
        // Used temporarily for rendering while dragging
        tempName: item.name
      }
    }
  }
}
</script>

<style scoped>
.component-palette {
  background-color: #fcfcfc;
  border-right: 1px solid #e0e0e0;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.palette-header {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.palette-header h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 500;
}

.palette-header p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

.palette-section {
  padding: 12px 16px;
}

.palette-section-title {
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
}

.palette-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 50px;
}

.palette-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: grab;
  transition: all 0.2s ease;
  user-select: none;
}

.palette-item:hover {
  border-color: #2196f3;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.palette-item:active {
  cursor: grabbing;
}

.palette-icon {
  margin-right: 12px;
  color: #555;
}

.palette-text {
  flex-grow: 1;
  font-size: 14px;
}

.drag-handle {
  color: #ccc;
  cursor: grab;
}

/* While dragging a cloned item, styles applied to the ghost object */
.sortable-ghost {
  opacity: 0.5;
  background-color: #e3f2fd;
}
</style>
